import type { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import { createChewyApiClient } from '@chewy/loyalty-graphql-client';
import { context } from '@opentelemetry/api';

import { HttpHeaders } from '@/constants/headers';
import logger from '@/utils/logger';
import { LOG_REQUEST_ID } from '@/utils/request-log-context';

import { createServerGraphQLTracingLink } from './server-graphql-tracing-link';

const COMPONENT_ID = 'loyalty-ui';

export type ServerGraphQLHeaderValue = string | null | undefined;

export interface CreateServerGraphQLClientOptions {
  headers?: Record<string, ServerGraphQLHeaderValue>;
  missingUrlWarning?: string;
  warnWhenMissing?: boolean;
}

export const cleanGraphQLHeaders = (
  headers?: Record<string, ServerGraphQLHeaderValue>,
): Record<string, string> => {
  if (!headers) return {};

  return Object.fromEntries(
    Object.entries(headers).filter((entry): entry is [string, string] => Boolean(entry[1])),
  );
};

export const createServerGraphQLClient = ({
  headers,
  missingUrlWarning,
  warnWhenMissing = true,
}: CreateServerGraphQLClientOptions = {}): ApolloClient<NormalizedCacheObject> | undefined => {
  const graphqlUrl = process.env.CHEWY_API_GRAPHQL_URL;

  if (!graphqlUrl) {
    if (warnWhenMissing && process.env.NODE_ENV === 'development' && missingUrlWarning) {
      logger.warn(missingUrlWarning);
    }
    return undefined;
  }

  const rid = context.active().getValue(LOG_REQUEST_ID);
  const client = createChewyApiClient({
    uri: graphqlUrl,
    headers: {
      'x-chewy-component-id': COMPONENT_ID,
      ...(typeof rid === 'string' ? { [HttpHeaders.RequestId]: rid } : {}),
      ...cleanGraphQLHeaders(headers),
    },
  });
  client.setLink(createServerGraphQLTracingLink().concat(client.link));
  return client;
};
